// Talks to our own server-side /api/chat function (never to NVIDIA directly).
// Reads the plain-text streamed response and delivers it token-by-token.

// Same-origin by default (Vercel). For local `vercel dev` on another port or a
// separately hosted backend, set REACT_APP_API_BASE.
const API_BASE = (process.env.REACT_APP_API_BASE || '').replace(/\/$/, '');
const CHAT_URL = `${API_BASE}/api/chat`;

const OVERALL_TIMEOUT_MS = 90000;

/**
 * Stream a chat completion from our backend.
 * @param {Array<{role:'user'|'assistant', content:string}>} messages recent turns, last is the user's
 * @param {{ onToken?: (chunk:string)=>void, signal?: AbortSignal }} opts
 * @returns {Promise<{ text: string }>} full text; throws on network/HTTP failure
 */
export async function streamChat(messages, opts = {}) {
  const { onToken, signal: externalSignal } = opts;
  const ctrl = new AbortController();
  const timeout = setTimeout(() => ctrl.abort(), OVERALL_TIMEOUT_MS);
  if (externalSignal) {
    if (externalSignal.aborted) ctrl.abort();
    else externalSignal.addEventListener('abort', () => ctrl.abort(), { once: true });
  }

  let text = '';
  try {
    const resp = await fetch(CHAT_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ messages }),
      signal: ctrl.signal,
    });

    if (!resp.ok || !resp.body) {
      const err = new Error(`Chat request failed (${resp.status})`);
      err.status = resp.status;
      throw err;
    }

    // Guard: the function replies as text/plain. If we somehow get HTML/JSON
    // (e.g. a dev server serving index.html for a missing /api route), treat it
    // as a failure so the local fallback answers instead of streaming markup.
    const contentType = resp.headers.get('content-type') || '';
    if (!contentType.includes('text/plain')) {
      throw new Error(`Unexpected chat content-type: ${contentType || 'none'}`);
    }

    const reader = resp.body.getReader();
    const decoder = new TextDecoder();
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      const chunk = decoder.decode(value, { stream: true });
      if (chunk) {
        text += chunk;
        if (onToken) onToken(chunk);
      }
    }
    return { text: text.trim() };
  } finally {
    clearTimeout(timeout);
  }
}
